import type { FC, ReactNode } from 'react'
import type { MessageContext } from '@/lib/whatsapp'
import type { WhatsAppOrder } from '@/components/shop/whatsapp-link'

import Link from 'next/link'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CircleCheck, ShoppingBag, Trash2, X } from 'lucide-react'
import { formatIDR } from '@/lib/format'
import { loadProductFeed } from '@/lib/product-feed'
import { buildCartMessage } from '@/lib/whatsapp'
import { useShop } from '@/components/providers/shop-provider'
import { Sheet } from '@/components/generals/sheet'
import { ProductImage } from '@/components/shop/product-image'
import { QuantitySelector } from '@/components/shop/shop-bits'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'

export const CartDrawer: FC = (): ReactNode => {
    const { cart, cartCount, cartTotal, isCartOpen, closeCart, updateQuantity, removeFromCart, clearCart, syncCart } = useShop()
    const [note, setNote] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [ordered, setOrdered] = useState<string | null>(null)

    // Check the latest stock and prices every time the cart opens.
    useEffect(() => {
        if (!isCartOpen || !cart.length) return

        let cancelled = false

        loadProductFeed({ fresh: true })
            .then(products => {
                if (!cancelled) syncCart(products)
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCartOpen])

    useEffect(() => {
        if (!isCartOpen) setOrdered(null)
    }, [isCartOpen])

    const message = useCallback((context: MessageContext) => buildCartMessage(context, { items: cart, note, customerName }), [cart, note, customerName])

    const order = useMemo<WhatsAppOrder>(() => ({
        source: 'keranjang',
        items: cart.map(item => ({ productId: item.productId, quantity: item.quantity, colorLabel: item.colorLabel })),
        customerName,
        note
    }), [cart, customerName, note])

    return (
        <Sheet open={isCartOpen} onClose={closeCart} labelledBy='cart-title'>
            <div className='flex items-center justify-between border-b border-border px-6 py-5'>
                <h2 id='cart-title' className='font-heading text-2xl text-cocoa-900'>
                    Keranjang
                    {cartCount > 0 && (
                        <span className='ml-2 align-middle text-sm font-semibold text-muted-foreground'>
                            <span>{cartCount}</span> produk
                        </span>
                    )}
                </h2>
                <button
                    type='button'
                    onClick={closeCart}
                    aria-label='Tutup keranjang'
                    className='grid size-10 place-items-center rounded-full text-cocoa-800 transition hover:bg-muted'
                >
                    <X className='size-5' />
                </button>
            </div>

            {cart.length === 0
                ? (
                    <div className='flex flex-1 flex-col items-center justify-center px-8 text-center'>
                        <div className='grid size-20 place-items-center rounded-full bg-blush-50'>
                            <ShoppingBag className='size-8 text-blush-500' />
                        </div>
                        <p className='mt-6 font-heading text-2xl text-cocoa-900'>
                            Keranjangmu masih kosong
                        </p>
                        <p className='mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground'>
                            Jelajahi butik atau lihat semua produk, lalu tambahkan boneka rajut yang kamu suka.
                        </p>
                        <Link
                            href='/shop'
                            onClick={closeCart}
                            className='mt-7 inline-flex h-12 items-center rounded-full bg-cocoa-800 px-6 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700'
                        >
                            Lihat Semua Produk
                        </Link>
                    </div>
                )
                : (
                    <>
                        <ul className='flex-1 divide-y divide-border overflow-y-auto px-6'>
                            {cart.map(item => (
                                <li key={item.key} className='flex gap-4 py-5'>
                                    <Link href={`/products/${item.slug}`} onClick={closeCart} className='shrink-0'>
                                        <ProductImage
                                            src={item.image}
                                            alt={item.name}
                                            designKey={item.designKey}
                                            mainColor={item.colorHex}
                                            accentColor={item.accentColor}
                                            className='aspect-square w-[84px] rounded-2xl'
                                        />
                                    </Link>
                                    <div className='flex min-w-0 flex-1 flex-col'>
                                        <div className='flex items-start justify-between gap-3'>
                                            <div className='min-w-0'>
                                                <Link href={`/products/${item.slug}`} onClick={closeCart} className='font-heading text-lg leading-snug text-cocoa-900 hover:underline'>
                                                    <span data-mdlfy-cms={`products:${item.productId}:name`}>
                                                        {item.name}
                                                    </span>
                                                </Link>
                                                <p className='mt-0.5 text-xs text-muted-foreground'>
                                                    {formatIDR(item.price)}
                                                    <span> / pcs</span>
                                                </p>
                                                {item.colorLabel && (
                                                    <p className='mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground'>
                                                        <span aria-hidden='true' className='size-3 rounded-full border border-black/10' style={{ background: item.colorHex ?? '#f3c9cf' }} />
                                                        {item.colorLabel}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type='button'
                                                onClick={() => removeFromCart(item.key)}
                                                aria-label={`Hapus ${item.name}`}
                                                className='grid size-8 shrink-0 place-items-center rounded-full text-cocoa-500 transition hover:bg-blush-50 hover:text-blush-600'
                                            >
                                                <Trash2 className='size-4' />
                                            </button>
                                        </div>
                                        <div className='mt-auto flex items-end justify-between gap-3 pt-3'>
                                            <div>
                                                <QuantitySelector
                                                    value={item.quantity}
                                                    max={item.maxQuantity}
                                                    onChange={value => updateQuantity(item.key, value)}
                                                    size='sm'
                                                />
                                                {item.maxQuantity !== null && item.quantity >= item.maxQuantity && (
                                                    <p className='mt-1 text-[11px] font-semibold text-blush-600'>
                                                        Stok tersisa <span>{item.maxQuantity}</span>
                                                    </p>
                                                )}
                                            </div>
                                            <p className='text-sm font-bold text-cocoa-800'>
                                                {formatIDR(item.price * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className='max-h-[60svh] overflow-y-auto border-t border-border bg-cream-100/70 px-6 pb-6 pt-5'>
                            {ordered
                                ? (
                                    <div className='rounded-3xl bg-white p-5 text-center shadow-sm'>
                                        <CircleCheck className='mx-auto size-9 text-sage-600' />
                                        <p className='mt-3 font-heading text-xl text-cocoa-900'>
                                            Pesanan <span>{ordered}</span> dibuat
                                        </p>
                                        <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                            Kirim pesan yang sudah terisi di WhatsApp agar kami bisa mengonfirmasi pesananmu.
                                        </p>
                                        <div className='mt-5 flex flex-col gap-2'>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    clearCart()
                                                    setNote('')
                                                    setOrdered(null)
                                                    closeCart()
                                                }}
                                                className='h-11 rounded-full bg-cocoa-800 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700'
                                            >
                                                Selesai, kosongkan keranjang
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => setOrdered(null)}
                                                className='h-11 rounded-full text-sm font-semibold text-cocoa-700 transition hover:bg-cream-100'
                                            >
                                                Kembali ke keranjang
                                            </button>
                                        </div>
                                    </div>
                                )
                                : (
                                    <>
                                        <div className='grid gap-3'>
                                            <div>
                                                <label htmlFor='cart-name' className='text-sm font-semibold text-cocoa-800'>
                                                    Nama pemesan
                                                    <span className='font-normal text-muted-foreground'> (opsional)</span>
                                                </label>
                                                <input
                                                    id='cart-name'
                                                    value={customerName}
                                                    onChange={event => setCustomerName(event.target.value)}
                                                    maxLength={80}
                                                    autoComplete='name'
                                                    placeholder='Nama kamu'
                                                    className='mt-2 h-11 w-full rounded-2xl border border-input bg-white px-4 text-sm outline-none transition focus:border-caramel-400 focus:ring-2 focus:ring-caramel-300/40'
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor='cart-note' className='text-sm font-semibold text-cocoa-800'>
                                                    Catatan untuk kami
                                                    <span className='font-normal text-muted-foreground'> (opsional)</span>
                                                </label>
                                                <textarea
                                                    id='cart-note'
                                                    value={note}
                                                    onChange={event => setNote(event.target.value)}
                                                    rows={2}
                                                    maxLength={500}
                                                    placeholder='Bungkus kado, nama untuk disulam, kota tujuan...'
                                                    className='mt-2 w-full resize-none rounded-2xl border border-input bg-white px-4 py-3 text-sm outline-none transition focus:border-caramel-400 focus:ring-2 focus:ring-caramel-300/40'
                                                />
                                            </div>
                                        </div>

                                        <dl className='mt-4 space-y-1.5 text-sm'>
                                            <div className='flex items-center justify-between text-muted-foreground'>
                                                <dt>Subtotal (<span>{cartCount}</span> produk)</dt>
                                                <dd>{formatIDR(cartTotal)}</dd>
                                            </div>
                                            <div className='flex items-center justify-between'>
                                                <dt className='font-semibold text-cocoa-800'>Total harga</dt>
                                                <dd className='font-heading text-2xl text-cocoa-900'>{formatIDR(cartTotal)}</dd>
                                            </div>
                                        </dl>
                                        <p className='mt-1 text-xs text-muted-foreground'>
                                            Ongkos kirim dan ketersediaan dikonfirmasi bersama kamu di WhatsApp.
                                        </p>

                                        <WhatsAppLink
                                            message={message}
                                            order={order}
                                            onOrdered={setOrdered}
                                            size='lg'
                                            className='mt-5 w-full'
                                        >
                                            Lanjutkan Pemesanan
                                        </WhatsAppLink>
                                        <button
                                            type='button'
                                            onClick={clearCart}
                                            className='mx-auto mt-3 block text-xs font-semibold text-cocoa-500 underline-offset-4 hover:text-cocoa-800 hover:underline'
                                        >
                                            Kosongkan keranjang
                                        </button>
                                    </>
                                )}
                        </div>
                    </>
                )}
        </Sheet>
    )
}
